
import { useContext, useState } from "react"
import { ImageRadioGroup, ReadOnlyBadge } from "@components"
import { meetingTypes } from "@config"
import { NotificationContext, UserContext } from "@context"
import { getGraphQLErrorMessage, hasExpired, mySqlToLocal } from "@helpers"
import { useUpdateEvent } from "@hooks"
import { useApolloClient } from "@apollo/client";
import { Button } from "@heroui/react"
import { sortBy } from "lodash"

import PropTypes from "prop-types"

const EventMeetingTypes = ({ backButton, event }) => {
  const initialFormData = {
    meetingTypeIds: event.meetingTypes.map(meetingType => parseInt(meetingType.id, 10))
  }

  /**
   * State
   */
  const [form, setForm] = useState({ ...initialFormData });
  const [savedFormData, setSavedFormData] = useState({ ...initialFormData });

  /**
   * Context
   */
  const { setCurrentEvent } = useContext(UserContext)
  const { notify } = useContext(NotificationContext)

  /**
   * Hooks
   */
  const client = useApolloClient();
  const {updateEvent, loading} = useUpdateEvent()

  /**
   * Functions
   */
  const handleChange = (selectedOptions) => {
    setForm(prev => ({ ...prev, meetingTypeIds: selectedOptions }))
  }

  const handleSave = () => {
    updateEvent({ 
      variables: {
        input: {
          id: event.id,
          meeting_type_ids: form.meetingTypeIds,
          validate_meeting_type_removals: true
        }
      },
      
      onCompleted: (response) => {
        notify({ title: "Success", type: "success", content: `Event updated successfully.`, open: true })
        setCurrentEvent(event.id) // forces user context to refresh event data
        client.refetchQueries({ include: "active" }); // refetch all active queries to reflect the new session
        setSavedFormData({ ...form })
      },

      onError: (error) => {
        notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(error, 'An error occurred while updating the event.'), open: true })
      }
    })
  }

  const isDirty = JSON.stringify(sortBy(form.meetingTypeIds)) !== JSON.stringify(sortBy(savedFormData.meetingTypeIds))
  const isReadOnlyMode = hasExpired(mySqlToLocal(event.end_date))
  const isSaveButtonDisabled = !isDirty || form.meetingTypeIds.length === 0

  return (
    <div>
      <div className="flex justify-end">
        {isReadOnlyMode && <ReadOnlyBadge className="mb-4" isFullWidth size="lg" />}
      </div>

      <p className="mb-4">Select & Deselect the meeting types you require for this event. You must have at least type selected.</p>

      {form.meetingTypeIds.length === 0 && <p className="mb-2 text-base font-semibold text-red-500">* You must select at least one meeting type.</p>}

      <ImageRadioGroup 
        classNames={{ container: "flex flex-col" }} 
        options={meetingTypes} 
        handleChange={(options) => handleChange(options)} 
        selectedOptionIds={form.meetingTypeIds} 
        isDisabled={isReadOnlyMode}
      />

      <Button className="mt-4 font-semibold" color="secondary" isDisabled={isSaveButtonDisabled} fullWidth onPress={() => handleSave()} isLoading={loading}>Save</Button>
      {backButton}
    </div>
  )
}

EventMeetingTypes.propTypes = {
  backButton: PropTypes.element.isRequired,
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    meetingTypes: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })).isRequired,  
  }).isRequired,
}

export default EventMeetingTypes